import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClientProvider } from 'react-query';
import { queryClient } from './queryClient';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import 'sweetalert2/dist/sweetalert2.min.css';

import Layout from './components/Layout';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Eleves from './pages/Eleves';
import Classes from './pages/Classes';
import Inscriptions from './pages/Inscriptions';
import Paiements from './pages/Paiements';
import EvaluationsPage from './pages/EvaluationsPage';
import Users from './pages/Users';
import Parents from './pages/Parents';
import Profile from './pages/Profile';
import Unauthorized from './pages/Unauthorized';
import Home from './pages/Home';
import RolesPermissions from './pages/RolesPermissions';
import TypeParentePage from './pages/TypeParentePage';
import { AuthProvider } from './contexts/AuthContext';
import { AcademicYearProvider } from './contexts/AcademicYearContext';
import PrivateRoute from './components/PrivateRoute';
import ProtectedRoute from './components/ProtectedRoute';
import ProfessionPage from './pages/ProfessionPage';
import ReligionPage from './pages/ReligionPage';
import OuverturesClassesPage from './pages/OuverturesClassesPage';
import Cycles from './pages/Cycles';
import BranchesPage from './pages/BranchesPage';
import PeriodesPage from './pages/PeriodesPage';
import MatieresPage from './pages/MatieresPage';
import TypeEvaluationsPage from './pages/TypeEvaluationsPage';
import ClasseMatieresPage from './pages/ClasseMatieresPage';
import ProfesseursPage from './pages/ProfesseursPage';
import NiveauQualifProfPage from './pages/NiveauQualifProfPage';
import ProfQualifPage from './pages/ProfQualifPage';
import QualificationsPage from './pages/QualificationsPage';
import IncidentsPage from './pages/IncidentsPage';
import AbsencesPage from './pages/AbsencesPage';
import EtablissementPage from './pages/EtablissementPage';
import AnneesScolairesPage from './pages/AnneesScolairesPage';
import NotificationsAdminPage from './pages/NotificationsAdminPage';
import RelevesNotesPage from './pages/RelevesNotesPage';
import FeeCategoriesPage from './pages/fees/FeeCategoriesPage';
import FeeStructuresPage from './pages/fees/FeeStructuresPage';
import FeePaymentsPage from './pages/fees/FeePaymentsPage';
import FeeOverduePage from './pages/fees/FeeOverduePage';
import FeeStatsPage from './pages/fees/FeeStatsPage';
import ParentAccountRequestPage from './pages/parent/ParentAccountRequestPage';
import ParentActivatePage from './pages/parent/ParentActivatePage';
import ParentDashboard from './pages/parent/ParentDashboard';
import ParentAppointmentsPage from './pages/parent/ParentAppointmentsPage';
import ParentEvaluationsPage from './pages/parent/ParentEvaluationsPage';
import ParentNotesPage from './pages/parent/ParentNotesPage';
import ParentDocumentsPage from './pages/parent/ParentDocumentsPage';
import ParentAbsencesPage from './pages/parent/ParentAbsencesPage';
import ParentFeesPage from './pages/parent/ParentFeesPage';
import ParentNotificationsPage from './pages/parent/ParentNotificationsPage';
import ParentMessagesPage from './pages/parent/ParentMessagesPage';
import ParentMessagesManagePage from './pages/ParentMessagesManagePage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import ParentNotifyBulletinsPage from './pages/ParentNotifyBulletinsPage';
import ParentAccountRequestsPage from './pages/ParentAccountRequestsPage';
import ParentAppointmentsManagePage from './pages/ParentAppointmentsManagePage';
import ParentLayout from './components/parent/ParentLayout';
import ParentPrivateRoute from './components/ParentPrivateRoute';

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <AcademicYearProvider>
          <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
              <div className="App">
                <Routes>
                  <Route path="/" element={<Home />} />
                  <Route path="/login" element={<Login />} />
                  <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                  <Route path="/reset-password" element={<ResetPasswordPage />} />
                  <Route path="/parent/request-account" element={<ParentAccountRequestPage />} />
                  <Route path="/parent/activate" element={<ParentActivatePage />} />
                  <Route path="/unauthorized" element={<Unauthorized />} />

                  {/* Espace parents */}
                  <Route path="/parent" element={<ParentPrivateRoute><ParentLayout /></ParentPrivateRoute>}>
                    <Route index element={<Navigate to="/parent/dashboard" replace />} />
                    <Route path="dashboard" element={<ParentDashboard />} />
                    <Route path="evaluations" element={<ParentEvaluationsPage />} />
                    <Route path="notes" element={<ParentNotesPage />} />
                    <Route path="documents" element={<ParentDocumentsPage />} />
                    <Route path="absences" element={<ParentAbsencesPage />} />
                    <Route path="fees" element={<ParentFeesPage />} />
                    <Route path="notifications" element={<ParentNotificationsPage />} />
                    <Route path="messages" element={<ParentMessagesPage />} />
                    <Route path="appointments" element={<ParentAppointmentsPage />} />
                  </Route>

                  <Route path="/app" element={<PrivateRoute><Layout /></PrivateRoute>}>
                    <Route index element={<Navigate to="/app/dashboard" replace />} />
                    <Route path="dashboard" element={
                      <ProtectedRoute allowedRoles={['admin', 'secretaire', 'intendant', 'enseignant']}>
                        <Dashboard />
                      </ProtectedRoute>
                    } />
                    <Route path="profile" element={
                      <ProtectedRoute allowedRoles={['admin', 'secretaire', 'intendant', 'enseignant']}>
                        <Profile />
                      </ProtectedRoute>
                    } />
                    
                    <Route path="eleves" element={
                      <ProtectedRoute allowedRoles={['admin', 'secretaire', 'intendant']}>
                        <Eleves />
                      </ProtectedRoute>
                    } />
                    
                    <Route path="parents" element={
                      <ProtectedRoute allowedRoles={['admin', 'secretaire', 'intendant']}>
                        <Parents />
                      </ProtectedRoute>
                    } />
                    
                    <Route path="classes" element={
                      <ProtectedRoute allowedRoles={['admin', 'secretaire', 'intendant']}>
                        <Classes />
                      </ProtectedRoute>
                    } />
                    <Route path="ouvertures-classes" element={
                      <ProtectedRoute allowedRoles={['admin', 'secretaire', 'intendant', 'enseignant']}>
                        <OuverturesClassesPage />
                      </ProtectedRoute>
                    } />
                    
                    <Route path="inscriptions" element={
                      <ProtectedRoute allowedRoles={['admin', 'secretaire', 'intendant']}>
                        <Inscriptions />
                      </ProtectedRoute>
                    } />
                    
                    <Route path="paiements" element={
                      <ProtectedRoute allowedRoles={['admin', 'secretaire', 'intendant']}>
                        <Paiements />
                      </ProtectedRoute>
                    } />
                    
                    <Route path="evaluations" element={
                      <ProtectedRoute allowedRoles={['admin', 'secretaire', 'intendant']}>
                        <EvaluationsPage />
                      </ProtectedRoute>
                    } />
                    
                    <Route path="users" element={
                      <ProtectedRoute allowedRoles={['admin']}>
                        <Users />
                      </ProtectedRoute>
                    } />
                    <Route path="roles-permissions" element={
                      <ProtectedRoute allowedRoles={['admin']}>
                        <RolesPermissions />
                      </ProtectedRoute>
                    } />
                    <Route path="types-parentes" element={
                      <ProtectedRoute allowedRoles={['admin', 'secretaire']}>
                        <TypeParentePage />
                      </ProtectedRoute>
                    } />
                    <Route path="professions" element={
                      <ProtectedRoute allowedRoles={['admin', 'secretaire']}>
                        <ProfessionPage />
                      </ProtectedRoute>
                    } />
                    <Route path="religions" element={
                      <ProtectedRoute allowedRoles={['admin', 'secretaire']}>
                        <ReligionPage />
                      </ProtectedRoute>
                    } />
                    <Route path="cycles" element={
                      <ProtectedRoute allowedRoles={['admin', 'secretaire']}>
                        <Cycles />
                      </ProtectedRoute>
                    } />
                    <Route path="branches" element={
                      <ProtectedRoute allowedRoles={['admin', 'secretaire']}>
                        <BranchesPage />
                      </ProtectedRoute>
                    } />
                    <Route path="periodes" element={
                      <ProtectedRoute allowedRoles={['admin', 'secretaire']}>
                        <PeriodesPage />
                      </ProtectedRoute>
                    } />
                    <Route path="matieres" element={
                      <ProtectedRoute allowedRoles={['admin', 'secretaire']}>
                        <MatieresPage />
                      </ProtectedRoute>
                    } />
                    <Route path="type-evaluations" element={
                      <ProtectedRoute allowedRoles={['admin', 'secretaire']}>
                        <TypeEvaluationsPage />
                      </ProtectedRoute>
                    } />
                    <Route path="classe-matieres" element={
                      <ProtectedRoute allowedRoles={['admin', 'secretaire']}>
                        <ClasseMatieresPage />
                      </ProtectedRoute>
                    } />
                    <Route path="professeurs" element={
                      <ProtectedRoute allowedRoles={['admin', 'secretaire']}>
                        <ProfesseursPage />
                      </ProtectedRoute>
                    } />
                    <Route path="niveau-qualif-prof" element={
                      <ProtectedRoute allowedRoles={['admin', 'secretaire']}>
                        <NiveauQualifProfPage />
                      </ProtectedRoute>
                    } />
                    <Route path="prof-qualif" element={
                      <ProtectedRoute allowedRoles={['admin', 'secretaire']}>
                        <ProfQualifPage />
                      </ProtectedRoute>
                    } />
                    <Route path="qualifications" element={
                      <ProtectedRoute allowedRoles={['admin', 'secretaire']}>
                        <QualificationsPage />
                      </ProtectedRoute>
                    } />
                    <Route path="absences" element={
                      <ProtectedRoute allowedRoles={['admin', 'secretaire', 'intendant']}>
                        <AbsencesPage />
                      </ProtectedRoute>
                    } />
                    <Route path="incidents" element={
                      <ProtectedRoute allowedRoles={['admin', 'secretaire']}>
                        <IncidentsPage />
                      </ProtectedRoute>
                    } /> 
                    <Route path="etablissement" element={
                      <ProtectedRoute allowedRoles={['admin']}>
                        <EtablissementPage />
                      </ProtectedRoute>
                    } /> 
                    <Route path="annees-scolaires" element={
                      <ProtectedRoute allowedRoles={['admin', 'secretaire']}>
                        <AnneesScolairesPage />
                      </ProtectedRoute>
                    } />
                    <Route path="notifications-admin" element={
                      <ProtectedRoute allowedRoles={['admin']}>
                        <NotificationsAdminPage />
                      </ProtectedRoute>
                    } /> 
                    <Route path="releves-notes" element={
                      <ProtectedRoute allowedRoles={['admin', 'secretaire', 'intendant']}>
                        <RelevesNotesPage />
                      </ProtectedRoute>
                    } />

                    {/* Frais de scolarité */}
                    <Route path="frais-scolarite/rubriques" element={
                      <ProtectedRoute allowedRoles={['admin', 'secretaire', 'intendant']}>
                        <FeeCategoriesPage />
                      </ProtectedRoute>
                    } />
                    <Route path="frais-scolarite/grilles" element={
                      <ProtectedRoute allowedRoles={['admin', 'secretaire', 'intendant']}>
                        <FeeStructuresPage />
                      </ProtectedRoute>
                    } />
                    <Route path="frais-scolarite/encaissements" element={
                      <ProtectedRoute allowedRoles={['admin', 'secretaire', 'intendant']}>
                        <FeePaymentsPage />
                      </ProtectedRoute>
                    } />
                    <Route path="frais-scolarite/impayes" element={
                      <ProtectedRoute allowedRoles={['admin', 'secretaire', 'intendant']}>
                        <FeeOverduePage />
                      </ProtectedRoute>
                    } />
                    <Route path="frais-scolarite/statistiques" element={
                      <ProtectedRoute allowedRoles={['admin', 'secretaire', 'intendant']}>
                        <FeeStatsPage />
                      </ProtectedRoute>
                    } />

                    <Route path="parent-account-requests" element={
                      <ProtectedRoute allowedRoles={['admin', 'secretaire', 'proviseur', 'directeur']}>
                        <ParentAccountRequestsPage />
                      </ProtectedRoute>
                    } />
                    <Route path="parent-appointments" element={
                      <ProtectedRoute allowedRoles={['admin', 'secretaire', 'proviseur', 'directeur']}>
                        <ParentAppointmentsManagePage />
                      </ProtectedRoute>
                    } />
                    <Route path="parent-notify-bulletins" element={
                      <ProtectedRoute allowedRoles={['admin', 'secretaire', 'proviseur', 'directeur']}>
                        <ParentNotifyBulletinsPage />
                      </ProtectedRoute>
                    } />
                    <Route path="parent-messages" element={
                      <ProtectedRoute allowedRoles={['admin', 'secretaire', 'proviseur', 'directeur']}>
                        <ParentMessagesManagePage />
                      </ProtectedRoute>
                    } />
                    </Route>
                  </Routes>
                <ToastContainer position="top-right" />
              </div>
            </Router>
          </AcademicYearProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
}

export default App;